import { useRef, useState } from "react";
import { api, ApiError, type Portfolio } from "../../lib/api";
import { slugify, cleanSlugInput } from "../../lib/slug";
import { NextStepCard } from "./Cards";

const HOST = window.location.hostname === "localhost" ? "professionals.test.wiebe.xyz" : window.location.hostname;


// ─── onboarding wizard ────────────────────────────────────────────────────────

export function OnboardingWizard({ onCreated }: { onCreated: (p: Portfolio) => void }) {
  const [step, setStep] = useState<"form" | "done">("form");

  // form fields
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugEdited, setSlugEdited] = useState(false);
  const [tagline, setTagline] = useState("");

  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<Portfolio | null>(null);

  // Starter page creation
  const [creatingPages, setCreatingPages] = useState<Set<string>>(new Set());
  const [createdPages, setCreatedPages] = useState<Set<string>>(new Set());

  const slugRef = useRef<HTMLInputElement>(null);

  async function createStarterPage(templateId: string, title: string) {
    setCreatingPages(prev => new Set([...prev, templateId]));
    try {
      const pageSlug = slugify(title, 40);
      await api.createPage({ slug: pageSlug, title, template_id: templateId });
      setCreatedPages(prev => new Set([...prev, templateId]));
    } catch {
      // ignore — non-fatal
    } finally {
      setCreatingPages(prev => { const s = new Set(prev); s.delete(templateId); return s; });
    }
  }

  function handleNameChange(v: string) {
    setName(v);
    if (!slugEdited) setSlug(slugify(v));
  }

  function handleSlugChange(v: string) {
    setSlugEdited(true);
    setSlug(cleanSlugInput(v));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const finalSlug = slugify(slug); // strip trailing hyphens before submit
    if (!name.trim() || !finalSlug) return;
    setCreating(true);
    setError(null);
    try {
      const p = await api.createPortfolio({
        slug: finalSlug,
        display_name: name.trim(),
        headline: tagline.trim() || undefined,
      });
      setCreated(p);
      setStep("done");
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setError("That URL is already taken — try a different one.");
        slugRef.current?.focus();
      } else {
        setError(err instanceof Error ? err.message : "Something went wrong");
      }
    } finally {
      setCreating(false);
    }
  }

  if (step === "done" && created) {
    const portfolioURL = `https://${HOST}/${created.Slug}?preview=true`;
    return (
      <div
        style={{
          maxWidth: 600,
          margin: "0 auto",
          padding: "2rem 0",
          textAlign: "center",
        }}
      >
        <div style={{ fontSize: "3rem", marginBottom: "0.75rem" }}>🎉</div>
        <h1 style={{ margin: "0 0 0.5rem", fontSize: "clamp(1.5rem, 4vw, 2.25rem)" }}>
          Your portfolio is live!
        </h1>
        <p style={{ color: "var(--muted)", marginBottom: "1.5rem" }}>
          Share this link — it's yours forever.
        </p>
        <a
          href={portfolioURL}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: "inline-block",
            padding: "0.75rem 1.5rem",
            background: "var(--accent)",
            color: "white",
            borderRadius: 10,
            fontWeight: 600,
            fontSize: "1.05rem",
            textDecoration: "none",
            marginBottom: "2rem",
            wordBreak: "break-all",
          }}
        >
          {HOST}/{created.Slug} ↗
        </a>

        <div style={{ display: "grid", gap: "0.75rem", textAlign: "left", marginBottom: "1.5rem" }}>
          <NextStepCard
            icon="📸"
            title="Create your first gallery"
            description="Upload photos from a shoot, then share a private link with your client."
            cta="Go to Galleries"
            href="/app/galleries"
          />
          <NextStepCard
            icon="✏️"
            title="Fill in your bio &amp; upload a photo"
            description="Your About page, logo, avatar, and contact email — it takes 2 minutes."
            cta="Edit portfolio"
            href="/app/portfolio"
          />
          <NextStepCard
            icon="🎨"
            title="Customise your colours and theme"
            description="Pick your brand colour and choose a layout template."
            cta="Open theme editor"
            href="/app/portfolio"
          />
        </div>

        <div className="card" style={{ marginBottom: "1.5rem", textAlign: "left" }}>
          <h3 style={{ marginTop: 0 }}>Create your page templates</h3>
          <p style={{ color: "var(--muted)", fontSize: "0.875rem" }}>Start with these common photographer pages, pre-filled and ready to edit.</p>
          <div style={{ display: "grid", gap: "0.5rem" }}>
            {([
              { template_id: "about-story", label: "About me", desc: "Your story and approach" },
              { template_id: "services-pricing", label: "Services & Pricing", desc: "Packages and what you offer" },
              { template_id: "process", label: "How I work", desc: "Walkthrough of your process" },
              { template_id: "testimonials", label: "Reviews", desc: "What clients say about you" },
            ] as const).map(({ template_id, label, desc }) => (
              <button
                key={template_id}
                className="btn-ghost"
                style={{ textAlign: "left", padding: "0.5rem 0.75rem" }}
                onClick={() => void createStarterPage(template_id, label)}
                disabled={creatingPages.has(template_id) || createdPages.has(template_id)}
              >
                {createdPages.has(template_id) ? `✓ ${label} created` : creatingPages.has(template_id) ? "Creating…" : `+ ${label} — ${desc}`}
              </button>
            ))}
          </div>
        </div>

        <button
          className="btn-primary"
          onClick={() => onCreated(created)}
          style={{ width: "100%" }}
        >
          Go to my dashboard
        </button>
      </div>
    );
  }

  const previewURL = `${HOST}/${slug || "your-name"}`;

  return (
    <div style={{ maxWidth: 620, margin: "0 auto", padding: "1.5rem 0" }}>
      {/* hero */}
      <div style={{ marginBottom: "2rem" }}>
        <h1
          style={{
            margin: "0 0 0.5rem",
            fontSize: "clamp(1.75rem, 5vw, 2.75rem)",
            lineHeight: 1.15,
            fontWeight: 800,
          }}
        >
          Your photography portfolio,{" "}
          <span style={{ color: "var(--accent)" }}>live in 60 seconds.</span>
        </h1>
        <p style={{ color: "var(--muted)", fontSize: "1.05rem", margin: 0 }}>
          Claim your URL, add your name, and you're online. Galleries, bookings,
          and client CRM included.
        </p>
      </div>

      <form onSubmit={handleSubmit} style={{ display: "grid", gap: "1.25rem" }}>
        {/* slug */}
        <div>
          <label
            style={{
              display: "block",
              fontWeight: 600,
              marginBottom: "0.4rem",
              fontSize: "0.875rem",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              color: "var(--muted)",
            }}
          >
            Your URL
          </label>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              background: "var(--card-bg)",
              border: "1.5px solid var(--border)",
              borderRadius: 10,
              overflow: "hidden",
            }}
          >
            <span
              style={{
                padding: "0.625rem 0.75rem",
                color: "var(--muted)",
                fontSize: "0.875rem",
                whiteSpace: "nowrap",
                borderRight: "1px solid var(--border)",
                userSelect: "none",
              }}
            >
              {HOST}/
            </span>
            <input
              ref={slugRef}
              required
              value={slug}
              onChange={(e) => handleSlugChange(e.target.value)}
              onBlur={(e) => setSlug(slugify(e.target.value))}
              placeholder="your-name"
              style={{
                flex: 1,
                border: "none",
                background: "transparent",
                padding: "0.625rem 0.75rem",
                fontSize: "1rem",
                outline: "none",
                fontWeight: 600,
                color: slug ? "var(--accent)" : undefined,
              }}
            />
          </div>
          {slug && (
            <p style={{ margin: "0.35rem 0 0", fontSize: "0.8rem", color: "var(--muted)" }}>
              Your public site will be at{" "}
              <strong style={{ color: "var(--text)" }}>{previewURL}</strong>
            </p>
          )}
        </div>

        {/* name */}
        <div>
          <label
            style={{
              display: "block",
              fontWeight: 600,
              marginBottom: "0.4rem",
              fontSize: "0.875rem",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              color: "var(--muted)",
            }}
          >
            Your name
          </label>
          <input
            required
            value={name}
            onChange={(e) => handleNameChange(e.target.value)}
            placeholder="Emma Jansen Photography"
            style={{ width: "100%", fontSize: "1rem", padding: "0.625rem 0.75rem" }}
          />
        </div>

        {/* tagline */}
        <div>
          <label
            style={{
              display: "block",
              fontWeight: 600,
              marginBottom: "0.4rem",
              fontSize: "0.875rem",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              color: "var(--muted)",
            }}
          >
            Tagline{" "}
            <span style={{ color: "var(--muted)", fontWeight: 400, textTransform: "none" }}>
              (optional)
            </span>
          </label>
          <input
            value={tagline}
            onChange={(e) => setTagline(e.target.value)}
            placeholder="Wedding &amp; portrait photographer in Amsterdam"
            style={{ width: "100%", fontSize: "1rem", padding: "0.625rem 0.75rem" }}
          />
        </div>

        {error && <div className="alert alert-error">{error}</div>}

        {/* what you get */}
        <div
          style={{
            background: "color-mix(in srgb, var(--accent) 8%, transparent)",
            border: "1px solid color-mix(in srgb, var(--accent) 25%, transparent)",
            borderRadius: 10,
            padding: "1rem 1.25rem",
          }}
        >
          <p
            style={{
              margin: "0 0 0.5rem",
              fontWeight: 600,
              fontSize: "0.875rem",
              color: "var(--accent)",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            Included for free
          </p>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
              gap: "0.35rem 1.5rem",
            }}
          >
            {[
              "4-page portfolio site (Home, Galleries, About, Contact)",
              "Private client galleries with share links",
              "Booking & inquiry management",
              "Lead inbox from your contact form",
              "Dark mode + custom colours",
              "Social OG cards for Instagram sharing",
            ].map((item) => (
              <p key={item} style={{ margin: 0, fontSize: "0.875rem", color: "var(--text)" }}>
                ✓ {item}
              </p>
            ))}
          </div>
        </div>

        <button
          type="submit"
          className="btn-primary"
          disabled={creating || !name.trim() || !slug}
          style={{ fontSize: "1.05rem", padding: "0.75rem 1.5rem", width: "100%" }}
        >
          {creating ? "Creating your portfolio…" : "Create my portfolio →"}
        </button>
      </form>
    </div>
  );
}

